import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { apiUrl } from "../api";

export type BulkActionResult = {
  terminalId: string;
  status: "pending" | "success" | "error";
  response: string;
};

type BulkActionContextType = {
  isVisible: boolean;
  isMinimized: boolean;
  isRunning: boolean;
  results: BulkActionResult[];
  actionLabel: string;
  setMinimized: (v: boolean) => void;
  dismiss: () => void;
  startBulkAction: (
    terminals: { terminalId: string }[],
    action: "enable" | "disable" | "suspend" | "reactivate",
    token: string
  ) => void;
};

const BulkActionContext = createContext<BulkActionContextType | null>(null);

export const useBulkAction = () => {
  const ctx = useContext(BulkActionContext);
  if (!ctx) throw new Error("useBulkAction must be used inside BulkActionProvider");
  return ctx;
};

export const BulkActionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<BulkActionResult[]>([]);
  const [actionLabel, setActionLabel] = useState("");

  // Keep a ref so the async loop always reads the latest token
  const tokenRef = useRef<string>("");

  const startBulkAction = useCallback(
    (terminals: { terminalId: string }[], action: "enable" | "disable" | "suspend" | "reactivate", token: string) => {
      tokenRef.current = token;
      const labelMap: Record<typeof action, string> = {
        enable: "Enable Menu",
        disable: "Disable Menu",
        suspend: "Suspend Terminals",
        reactivate: "Reactivate Terminals",
      };
      const label = labelMap[action];
      setActionLabel(label);

      const initial: BulkActionResult[] = terminals.map((t) => ({
        terminalId: t.terminalId,
        status: "pending",
        response: "Waiting for terminal...",
      }));

      setResults(initial);
      setIsVisible(true);
      setIsMinimized(false);
      setIsRunning(true);

      const promises = terminals.map((terminal, i) => {
        const tid = terminal.terminalId;
        const url = (action === "suspend" || action === "reactivate")
          ? `${apiUrl}/msp/terminals/${encodeURIComponent(tid)}/${action}`
          : `${apiUrl}/terminals/enable-menu/${encodeURIComponent(tid)}/${action}`;
        return fetch(
          url,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenRef.current}`,
            },
            credentials: "include",
          }
        )
          .then(async (res) => {
            const text = await res.text();
            let msg = text;
            try { msg = JSON.parse(text)?.message ?? text; } catch { /* keep raw */ }
            return { i, terminalId: tid, status: res.ok ? "success" as const : "error" as const, response: res.ok ? "Success" : (msg || `Error ${res.status}`) };
          })
          .catch((err: any) => ({
            i,
            terminalId: tid,
            status: "error" as const,
            response: err?.message || "Network error",
          }))
          .then((result) => {
            setResults((prev) => {
              const next = [...prev];
              next[result.i] = { terminalId: result.terminalId, status: result.status, response: result.response };
              return next;
            });
            return result;
          });
      });

      Promise.all(promises).then(() => setIsRunning(false));
    },
    []
  );

  const dismiss = useCallback(() => {
    if (isRunning) return;
    setIsVisible(false);
    setResults([]);
  }, [isRunning]);

  return (
    <BulkActionContext.Provider
      value={{ isVisible, isMinimized, isRunning, results, actionLabel, setMinimized: setIsMinimized, dismiss, startBulkAction }}
    >
      {children}
    </BulkActionContext.Provider>
  );
};
